import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import axios from "axios";
import { ComplianceScoreBadge } from "@/components/ComplianceScoreBadge";
import { FolderSimple, DownloadSimple, ShieldCheck, LockKey } from "@phosphor-icons/react";

const kb = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function DataRoomShared() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState({ loading: true, error: "" });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/dataroom/${token}`);
        setData(r.data); setState({ loading: false, error: "" });
      } catch {
        setState({ loading: false, error: "This data room link is not available or has been revoked." });
      }
    })();
  }, [token]);

  const download = (fid, name) => {
    const url = `${API}/public/dataroom/${token}/file/${fid}`;
    const a = document.createElement("a"); a.href = url; a.download = name; a.target = "_blank"; a.click();
  };

  if (state.loading) return (
    <div className="min-h-screen aurora flex items-center justify-center">
      <div className="glass rounded-2xl px-10 py-8 text-center"><div className="eyebrow mb-3">Opening data room…</div><span className="dot-flash" /><span className="dot-flash" /><span className="dot-flash" /></div>
    </div>
  );
  if (state.error) return (
    <div className="min-h-screen aurora flex items-center justify-center px-6">
      <div className="glass rounded-2xl px-10 py-10 text-center max-w-md">
        <LockKey size={40} weight="duotone" className="text-white/40 mx-auto mb-4" />
        <div className="font-display text-xl mb-2">Link unavailable</div>
        <p className="text-white/55 text-sm">{state.error}</p>
      </div>
    </div>
  );

  const filesByFolder = (folder) => data.files.filter((f) => f.folder === folder);

  return (
    <div className="min-h-screen aurora py-10 px-4 sm:px-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow mb-1">Data Room</div>
            <h1 className="font-display text-3xl">{data.company_name}</h1>
          </div>
          <div className="flex items-center gap-2 text-white/50 text-xs"><ShieldCheck size={16} className="text-[#00A86B]" /> Read-only · shared via PerCen360</div>
        </div>

        <ComplianceScoreBadge health={data.health} />

        <div className="glass rounded-2xl p-5 flex items-center justify-between">
          <span className="text-sm text-white/70">Due-diligence completeness</span>
          <div className="flex items-center gap-3">
            <div className="w-40 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-[#00A86B]" style={{ width: `${data.checklist.pct}%` }} /></div>
            <span className="mono text-[#FFB020] text-sm" data-testid="shared-dd-pct">{data.checklist.pct}%</span>
          </div>
        </div>

        {data.folders.map((f) => {
          const files = filesByFolder(f.name);
          return (
            <div key={f.name} className="glass rounded-2xl p-6" data-testid={`shared-folder-${f.name}`}>
              <div className="flex items-center gap-2 mb-3"><FolderSimple size={20} weight="duotone" className="text-[#FFB020]" /><span className="font-display">{f.name}</span><span className="text-white/40 text-xs">· {f.count} file{f.count === 1 ? "" : "s"}</span></div>
              {files.length === 0 ? <div className="text-white/35 text-sm">No documents shared in this folder.</div> : files.map((file) => (
                <div key={file.file_id} className="flex items-center justify-between py-2 border-b border-white/5" data-testid={`shared-file-${file.file_id}`}>
                  <div className="min-w-0"><div className="text-sm truncate">{file.name}</div><div className="text-white/40 text-xs">{kb(file.size)}</div></div>
                  <button onClick={() => download(file.file_id, file.name)} className="btn-ghost text-xs shrink-0" data-testid={`shared-dl-${file.file_id}`}><DownloadSimple size={14} /> Download</button>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
